import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';

const LANGUAGE_STORAGE_KEY = 'fastri.language';
const SUPPORTED_LANGUAGES = ['fr', 'en', 'ar', 'de'];

function getBrowserLanguage() {
  if (typeof navigator === 'undefined') return null;
  // navigator.language returns BCP 47 tags like "en-US", "fr-FR", "ar"
  const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
  if (browserLang && SUPPORTED_LANGUAGES.includes(browserLang)) return browserLang;
  return null;
}

function getInitialLanguage() {
  // 1. Respect explicit user choice stored in localStorage
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch (_) {}
  // 2. Detect browser language
  const browserLang = getBrowserLanguage();
  if (browserLang) return browserLang;
  // 3. Default to English
  return 'en';
}

function applyDocumentLanguage(lng) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
}

const ALL_NAMESPACES = [
  'common',
  'auth',
  'billing',
  'chat',
  'consumption',
  'economic',
  'electrical',
  'environmental',
  'equipment',
  'feedback',
  'meteo',
  'profile',
  'project',
  'quote',
  'report',
  'results',
  'settings',
  'team',
];

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    // Load all namespaces to ensure translations work throughout the app
    // The JSON files are small and cached by the browser
    ns: ALL_NAMESPACES,
    defaultNS: 'common',
    fallbackNS: ALL_NAMESPACES,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      // Use suspense to ensure translations are loaded before rendering
      useSuspense: true,
    },
  });

applyDocumentLanguage(i18n.language);
i18n.on('languageChanged', (lng) => {
  try {
    if (SUPPORTED_LANGUAGES.includes(lng)) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    }
  } catch (_) {}
  applyDocumentLanguage(lng);
});

/**
 * Preload specific namespaces for a component.
 * Use this when you need to ensure translations are available before rendering.
 * @param {string[]} namespaces - Array of namespace names to load
 * @returns {Promise<void>}
 */
export function preloadNamespaces(namespaces) {
  return i18n.loadNamespaces(namespaces);
}

// Export for use in other modules
export { ALL_NAMESPACES, SUPPORTED_LANGUAGES };

export default i18n;
